export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * ファイルサイズをチェック
 */
export function validateFileSize(
  file: File,
  maxSizeMB: number = 10
): FileValidationResult {
  const maxBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `ファイルサイズが制限を超えています（最大${maxSizeMB}MB）`,
    };
  }

  return { valid: true };
}

/**
 * ファイル形式をチェック
 * ワイルドカード（image/*, video/*）に対応
 */
export function validateFileType(
  file: File,
  allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
): FileValidationResult {
  const fileType = file.type?.toLowerCase() ?? '';
  const fileName = file.name?.toLowerCase() ?? '';

  // ワイルドカードまたは完全一致でチェック
  const isAllowed = allowedTypes.some((allowedType) => {
    const normalizedAllowed = allowedType.toLowerCase();

    // 拡張子指定（.pdf など）
    if (normalizedAllowed.startsWith('.')) {
      return fileName.endsWith(normalizedAllowed);
    }

    // ワイルドカード対応（例: image/* は image/png, image/jpeg などにマッチ）
    if (normalizedAllowed.endsWith('/*')) {
      const prefix = normalizedAllowed.slice(0, -2); // "image/*" -> "image/"
      return fileType.startsWith(prefix);
    }
    // 完全一致
    return fileType === normalizedAllowed;
  });

  if (!isAllowed) {
    return {
      valid: false,
      error: `サポートされていないファイル形式です（${allowedTypes.join(', ')}）`,
    };
  }

  return { valid: true };
}

/**
 * 総合的なファイルバリデーション
 */
export function validateFile(
  file: File,
  options?: {
    maxSizeMB?: number;
    allowedTypes?: string[];
  }
): FileValidationResult {
  const sizeValidation = validateFileSize(file, options?.maxSizeMB);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  const typeValidation = validateFileType(file, options?.allowedTypes);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  return { valid: true };
}
